<template>
  <div class="general-file-uploader">
    <b-form-file
        v-model="file"
        :state="Boolean(file)"
        :accept="config.allowed_extensions"
      :placeholder="$t('common.file_upload_placeholder')"
      :drop-placeholder="$t('common.file_drop_placeholder')"
    ></b-form-file>
    <selected-file-progress :file="file" :progress-value="progressValue" :error="errorMessage" :success="successMessage"/>
  </div>
</template>

<script>
import SelectedFileProgress from "@/modules/FileUploader/partials/SelectedFileProgress.vue";
import axios from "@/plugins/axios";

export default {
  name: 'FileUploader',
  components: {SelectedFileProgress},
  props: ['config', 'params', 'headers'],
  data() {
    return {
      file: null,
      progressValue: 100,
      errorMessage: null,
      successMessage: null,
      requestParams: this.params ?? {},
    }
  },
  computed: {
    requestHeaders() {
      return {
        headers: this.headers ?? {},
        onUploadProgress: progressEvent => this.onUploadProgress(progressEvent)
      };
    },
    maxFileSizeBytes() {
      const mb = Number(this.config?.max_file_size_mb);
      if (!Number.isFinite(mb) || mb <= 0) {
        return null;
      }
      return mb * 1024 * 1024;
    },
    forbiddenExtensions() {
      const list = this.config?.forbidden_extensions;
      if (!list) {
        return [];
      }
      if (Array.isArray(list)) {
        return list.map((ext) => String(ext).toLowerCase());
      }
      return String(list)
        .split(",")
        .map((ext) => ext.trim().toLowerCase())
        .filter((ext) => ext.length > 0);
    }
  },
  methods: {
    clearSelectedFiles() {
      this.file = null;
      this.reset();
    },
    reset() {
      this.progressValue = 0;
      this.errorMessage = null;
      this.successMessage = null;
      this.requestParams = null;
    },
    setFileError(message) {
      this.progressValue = 0;
      this.errorMessage = message;
      this.successMessage = null;
    },
    validateFileSize(file) {
      const limit = this.maxFileSizeBytes;
      if (!limit || !file) {
        return true;
      }
      if (file.size <= limit) {
        return true;
      }

      const maxMb = Number(this.config?.max_file_size_mb) || 0;
      const message = this.$t ? this.$t('common.file_too_large', { max: maxMb }) : `File is too large. Max ${maxMb} MB.`;
      this.setFileError(message);
      return false;
    },
    validateFileExtension(file) {
      if (!file) {
        return true;
      }

      const forbidden = this.forbiddenExtensions;
      if (!forbidden.length) {
        return true;
      }

      const name = (file.name || "").toLowerCase();
      const blocked = forbidden.find((ext) => name.endsWith(ext));
      if (!blocked) {
        return true;
      }

      const message = this.$t ? this.$t('common.file_type_not_allowed') : "File type is not allowed.";
      this.setFileError(message);
      return false;
    },
    upload(requestParams = {}) {
      let vm = this;
      if (!this.file || !this.validateFileExtension(this.file) || !this.validateFileSize(this.file)) {
        vm.$emit('onFileUploadError', { message: this.errorMessage || 'File validation failed.' });
        return;
      }
      let formData = new FormData();
      formData.append('file', this.file, this.file.name);

      Object.keys(requestParams).forEach((key) => {
        formData.append(key, requestParams[key]);
      })

      axios.post(this.config.upload_url, formData, this.requestHeaders)
          .then(response => {
            vm.$emit('onFileUploaded', response.data.data);
            vm.successMessage = response.data.data.message;
          })
          .catch(error => {
            vm.$emit('onFileUploadError', error);
            if (error.response.data && error.response.data.message) {
              vm.errorMessage = error.response.data.message;
            } else {
              vm.errorMessage = error;
            }

            console.error('onFileUploadError: ', error);
          });
    },
    onUploadProgress(e) {
      this.progressValue = Math.round( (e.loaded * 100) / e.total );
    },
    removeFile() {
      this.clearSelectedFiles();
    }
  },
  mounted() {
    document.querySelector('.b-form-file input').style.display = 'none';
  },
  watch: {
    file: function(file) {
      this.reset();
      if (file) {
        if (!this.validateFileExtension(file) || !this.validateFileSize(file)) {
          return;
        }
        this.$emit('onFileSelected', file)
      } else {
        this.$emit('onFileRemoved', file)
      }
    },
    params: {
      handler: function (newVal) {
        this.requestParams = newVal;
      },
      deep: true,
    }
  }
}
</script>

<style scoped>

</style>
